import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
    usePendingHairSalonExternalReservations,
    usePendingHairSalonHotelReservations,
} from '@/components/ReservationContext'
import { HotelReservationViewer } from '@/features/reservation-viewer/ui/HotelReservationViewer'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card'
import { useToast } from '@/shared/ui/use-toast'
import type { Reservation, UnscheduledHairSalonTask } from '@monorepo/functions/src/types/reservations'

import { useUnscheduledHairSalonTasks } from '../model/hooks'
import { useCalendarStore } from '../model/store'
import { areSameUnscheduledTask, isTaskUnscheduled } from '../model/task-utils'
import { HairSalonReservationViewer } from './HairSalonReservationViewer'
import { UnscheduledHairSalonTaskCard } from './UnscheduledHairSalonTaskCard'

interface UnscheduledReservationsProps {
    className?: string
    managingReservationId?: string | null
}

export function UnscheduledHairSalonTasks({ className, managingReservationId }: UnscheduledReservationsProps) {
    const { selectedTask, setSelectedTask } = useCalendarStore()
    const { pendingReservations: externalReservations, isLoading: isLoadingExternalReservations } =
        usePendingHairSalonExternalReservations()
    const { pendingReservations: hotelReservations, isLoading: isLoadingHotelReservations } =
        usePendingHairSalonHotelReservations()
    const unscheduledReservations = useMemo(() => {
        return [...externalReservations, ...hotelReservations]
            .filter(r => (managingReservationId ? r.id === managingReservationId : true))
            .sort((r1, r2) => {
                if (r1.type === 'hotel' && r2.type === 'hotel') {
                    // Both hotel: sort by checkOutDate
                    return r1.checkOutDate.localeCompare(r2.checkOutDate)
                }
                if (r1.type === 'peluqueria' && r2.type === 'peluqueria') {
                    // Both peluqueria: sort by date
                    return r1.date.localeCompare(r2.date)
                }
                // Hotel reservations come first
                if (r1.type === 'hotel') return -1
                if (r2.type === 'hotel') return 1
                return 0
            })
    }, [externalReservations, hotelReservations, managingReservationId])
    
    const isLoading = isLoadingExternalReservations || isLoadingHotelReservations
    const { unscheduledTasks } = useUnscheduledHairSalonTasks(unscheduledReservations)
    const [selectedModalReservation, setSelectedModalReservation] = useState<Reservation | null>(null)
    const { toast } = useToast()
    const { t } = useTranslation()
    const handleTaskClick = (task: UnscheduledHairSalonTask, e: React.MouseEvent) => {
        // Si es parte de un doble clic, no hacemos nada
        if (e.detail === 2) {
            return
        }

        if (selectedTask && isTaskUnscheduled(selectedTask) && areSameUnscheduledTask(selectedTask, task)) {
            setSelectedTask(null)
        } else {
            setSelectedTask(task)
            toast({
                title: 'Cita seleccionada',
                description: 'Selecciona el hueco donde quieres crear las tareas.',
            })
        }
    }

    const handleTaskDoubleClick = (task: UnscheduledHairSalonTask, e: React.MouseEvent) => {
        e.preventDefault() // Prevenir el doble clic del sistema
        e.stopPropagation()
        setSelectedModalReservation(task.reservation)
    }
    if (!isLoading && unscheduledTasks.length === 0) {
        return null
    }

    return (
        <>
            <Card className={className}>
                <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
                    <CardTitle>Citas sin hora asignada</CardTitle>
                </CardHeader>
                <CardContent className='py-2'>
                    {isLoading ? (
                        <div className='flex h-full items-center justify-center text-sm text-gray-500'>
                            {t('unscheduledReservations.loading', 'Cargando citas sin hora asignada...')}
                        </div>
                    ) : (
                        <div className='h-fit overflow-x-auto p-2'>
                            {unscheduledTasks.length > 0 ? (
                                <div className='flex h-fit gap-2 pb-2' style={{ minWidth: 'min-content' }}>
                                    {unscheduledTasks.map(unscheduledTask => {
                                        return (
                                            <UnscheduledHairSalonTaskCard
                                                key={`${unscheduledTask.reservation.id}_${unscheduledTask.petIndex}_${unscheduledTask.service}`}
                                                task={unscheduledTask}
                                                isSelected={
                                                    !!selectedTask &&
                                                    isTaskUnscheduled(selectedTask) &&
                                                    areSameUnscheduledTask(selectedTask, unscheduledTask)
                                                }
                                                onTaskClick={handleTaskClick}
                                                onTaskDoubleClick={handleTaskDoubleClick}
                                            />
                                        )
                                    })}
                                </div>
                            ) : (
                                <div className='flex h-full items-center justify-center text-sm text-gray-500'>
                                    {t('unscheduledReservations.none', 'No hay citas sin asignar')}
                                </div>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>

            {selectedModalReservation &&
                (selectedModalReservation.type === 'peluqueria' ? (
                    <HairSalonReservationViewer
                        reservationId={selectedModalReservation.id}
                        isOpen={true}
                        onClose={() => {
                            setSelectedModalReservation(null)
                        }}
                    />
                ) : (
                    <HotelReservationViewer
                        reservation={selectedModalReservation}
                        isOpen={true}
                        onClose={() => {
                            setSelectedModalReservation(null)
                        }}
                    />
                ))}
        </>
    )
}
